import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import contactImage from '../../assets/Contact/connect.jpg';
import './Contact.css';

const FuturisticContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    query: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post('https://altrixsoftech.com/api/contact-send', formData);
      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Message Sent!',
          text: '✅ We will contact you soon!',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3085d6',
          background: '#0a0e17',
          color: '#ffffff'
        });
        setFormData({ name: '', email: '', phone: '', subject: '', query: '' });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Send',
        text: '❌ Something went wrong. Please try again.',
        background: '#0a0e17',
        color: '#ffffff'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Floating Orbs Background
  const FloatingOrbs = () => (
    <div className="orb-container">
      <div className="floating-orb orb-1"></div>
      <div className="floating-orb orb-2"></div>
      <div className="floating-orb orb-3"></div>
      <div className="grid-overlay"></div>
      <div className="scan-lines"></div>
    </div>
  );

  return (
    <div className="futuristic-contact-page">
      <FloatingOrbs />
      
      <motion.div 
        className="contact-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Main Contact Card */}
        <div className="futuristic-contact-card glass-card">
          
          {/* Left Side: Image - Made smaller and more beautiful */}
          <motion.div 
            className="contact-image-side"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="image-container">
              <img src={contactImage} alt="Contact" className="contact-image" />
              <div className="image-overlay"></div>
              
              {/* Floating decorative elements */}
              <div className="floating-elements">
                <div className="floating-element el-1">
                  <div className="element-glow"></div>
                </div>
                <div className="floating-element el-2">
                  <div className="element-glow"></div>
                </div>
                <div className="floating-element el-3">
                  <div className="element-glow"></div>
                </div>
              </div>

              {/* Contact Info Overlay */}
              <div className="contact-overlay-content">
                <h3 className="overlay-title">Let's Connect</h3>
                <p className="overlay-text">Start your journey with us today</p>
                <div className="overlay-features">
                  <div className="feature-item">
                    <span className="feature-icon">⚡</span>
                    <span>Quick Response</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">💼</span>
                    <span>Professional Support</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">🚀</span>
                    <span>24/7 Available</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side: Form */}
          <motion.div 
            className="contact-form-side"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <form className="futuristic-contact-form" onSubmit={handleSubmit}>
              <motion.h2 
                className="gradient-text"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                Get in Touch
              </motion.h2>
              
              <motion.p 
                className="form-subtitle text-secondary"
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                Ready to start your journey? Let's connect and build something amazing.
              </motion.p>

              <div className="form-grid">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <div className="input-group">
                    <i className="input-icon fas fa-user"></i>
                    <input 
                      type="text" 
                      name="name" 
                      placeholder="Full Name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      required 
                      className="glass-input"
                    />
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                >
                  <div className="input-group">
                    <i className="input-icon fas fa-envelope"></i>
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Email Address" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                      className="glass-input"
                    />
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                >
                  <div className="input-group">
                    <i className="input-icon fas fa-phone"></i>
                    <input 
                      type="text" 
                      name="phone" 
                      placeholder="Phone Number" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      required 
                      className="glass-input"
                    />
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.1 }}
                >
                  <div className="input-group">
                    <i className="input-icon fas fa-tag"></i>
                    <input 
                      type="text" 
                      name="subject" 
                      placeholder="Subject" 
                      value={formData.subject} 
                      onChange={handleChange} 
                      required 
                      className="glass-input"
                    />
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                className="textarea-container"
              >
                <div className="input-group">
                  <i className="input-icon fas fa-comment"></i>
                  <textarea 
                    name="query" 
                    placeholder="Your Message" 
                    value={formData.query} 
                    onChange={handleChange} 
                    required 
                    className="glass-input"
                    rows="4"
                  ></textarea>
                </div>
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.3 }}
              >
                <button 
                  type="submit" 
                  className={`futuristic-btn submit-button ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="btn-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane btn-icon"></i>
                      Send Message 
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>
              </motion.div>
            </form>
          </motion.div>
        </div>

        {/* Contact Information Section */}
        <motion.div 
          className="futuristic-contact-info glass-card"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h3 className="gradient-text-blue">Contact Information</h3>
          
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">
                <i className="fas fa-phone"></i>
              </div>
              <div className="info-content">
                <h4>Phone</h4>
                <p className="text-secondary">+917827856754</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="info-content">
                <h4>Email</h4>
                <p className="text-secondary">
                  <a href="mailto:sales@naught.co.in">sales@naught.co.in</a>
                </p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="info-content">
                <h4>Address</h4>
                <p className="text-secondary">H-141, Sector 63, Noida, U.P 201301</p>
              </div>
            </div>
          </div>

          <div className="social-section">
            <h4 className="text-secondary">Follow Us</h4>
            <div className="futuristic-social-icons">
              <motion.a 
                href="https://www.linkedin.com/company/naught-ventures/" 
                target="_blank" 
                rel="noreferrer"
                whileHover={{ scale: 1.2, y: -5 }}
                className="social-icon linkedin"
              >
                <i className="fab fa-linkedin"></i>
              </motion.a>
              <motion.a 
                href="https://www.instagram.com/naught_vc/" 
                target="_blank" 
                rel="noreferrer"
                whileHover={{ scale: 1.2, y: -5 }}
                className="social-icon instagram"
              >
                <i className="fab fa-instagram"></i>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default FuturisticContactForm;